import { useEffect, useMemo, useState, useCallback } from "react";
import { useFocusEffect } from "@/components/hooks/use-focus-effect";
import type {
  DurationOption,
  FrequencyOption,
} from "@/components/shared/service-radio-item/types";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import {
  PackageRecurrenceEnum,
  ServiceTypeEnum,
} from "@/__generated__/graphql";
import {
  getBookingPackages,
  getDateRangeFromFrequency,
} from "@/lib/booking-lib";
import { useBookingRoute } from "@/components/hooks/use-booking-route";
import { useBookingState } from "@/store/booking/useBookingState";
import {
  HomeCleaningFrequencyOptionItems,
  HomeCleaningFrequencyOptions,
} from "@/constants/booking";
import { SelectServices } from "../../select-services";
import { FindBestHelperModal } from "@/components/shared/find-best-helper-modal";
import { PackageFrequencyGroupEnum } from "@/types/booking";
import { View } from "@/components/ui";
import { CleaningDurationModal } from "../../home-cleaning/cleaning-duration-modal";
import { HomeCleaningToolsCard } from "@/components/shared/home-cleaning-tools-card";

export function HomeCleaningBooking() {
  const {
    data: { department, packageDetailsFilter, serviceDurationModalOpen },
    setBookingState,
  } = useBookingState();
  const [findHelperModalOpen, setFindHelperModalOpen] = useState(false);
  const { forward } = useBookingRoute();

  const homeCleaningSelectedFrequencyOption = useMemo(() => {
    return (
      HomeCleaningFrequencyOptions.flat().find(
        (opt) => opt.frequency === packageDetailsFilter.frontEndFrequency,
      ) || HomeCleaningFrequencyOptions[0][0]
    );
  }, [packageDetailsFilter.frontEndFrequency]);

  useFocusEffect(
    useCallback(() => {
      const currentFrequency =
        useBookingState.getState().data.packageDetailsFilter.frontEndFrequency;

      if (currentFrequency) {
        // Coming back from a deeper screen — just restore the date range for the current frequency
        const [startDate, endDate] =
          getDateRangeFromFrequency(currentFrequency);
        setBookingState({ startDate, endDate });
      } else {
        // First visit — initialize to default frequency
        const initialFrequency = PackageFrequencyGroupEnum.WEEKLY;
        const [startDate, endDate] =
          getDateRangeFromFrequency(initialFrequency);
        setBookingState({
          packageDetailsFilter: {
            serviceTypes: [ServiceTypeEnum.HomeCleaning],
            repeatEvery: [PackageRecurrenceEnum.Week],
            repeatEveryTimes: 1,
            frontEndFrequency: initialFrequency,
            duration: 0,
          },
          startDate,
          endDate,
        });
      }
    }, []),
  );

  useEffect(() => {
    if (serviceDurationModalOpen) {
      setBookingState({
        packageDetailsFilter: {
          ...packageDetailsFilter,
          duration: 4,
        },
      });
    }
  }, [serviceDurationModalOpen]);

  const onChooseCleaningFrequency = (item: FrequencyOption) => {
    if (item.openModal) {
      setFindHelperModalOpen(true);
      return;
    }
    const [startDate, endDate] = getDateRangeFromFrequency(item.frequency);
    setBookingState({
      packageDetailsFilter: {
        ...packageDetailsFilter,
        repeatEvery: [item.repeatEvery],
        repeatEveryTimes: item.repeatEveryTimes,
        frontEndFrequency: item.frequency,
      },
      startDate,
      endDate,
    });
  };

  const { fetch: getAllClientPackage, loading } =
    useAllClientPackageDetailsStore();

  const getPackages = () => {
    const { frontEndFrequency: _frontEndFrequency, ...filters } =
      packageDetailsFilter;

    return getAllClientPackage({
      requestPayload: {
        filters: {
          ...filters,
          department,
          active: true,
        },
      },
    }).then((res) => {
      if (res.error || !res.data) {
        return;
      }
      const packages = getBookingPackages(department, res.data);
      setBookingState({ selectedPackageIds: packages.map((pkg) => pkg.id) });
    });
  };

  const onSelectDuration = (option: DurationOption) =>
    setBookingState({
      packageDetailsFilter: {
        ...packageDetailsFilter,
        duration: option.duration,
      },
    });

  const onKeepOneTime = () => {
    getPackages().then(() => {
      forward();
    });
  };

  return (
    <View>
      <HomeCleaningToolsCard />

      <SelectServices
        selectedFrequencyOption={homeCleaningSelectedFrequencyOption}
        cleaningFrequencyOptions={HomeCleaningFrequencyOptionItems}
        onChooseCleaningFrequency={onChooseCleaningFrequency}
      />

      <CleaningDurationModal
        open={serviceDurationModalOpen}
        setOpen={(open: boolean) =>
          setBookingState({ serviceDurationModalOpen: open })
        }
        selectedFrequencyOption={homeCleaningSelectedFrequencyOption}
        selectedDurationOptionValue={String(packageDetailsFilter.duration)}
        onSelectDurationOption={onSelectDuration}
        onProceed={onKeepOneTime}
        loading={loading}
      />

      <FindBestHelperModal
        open={findHelperModalOpen}
        setOpen={setFindHelperModalOpen}
        name="home-cleaning"
      />
    </View>
  );
}
